import { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.jsx';
import { Input } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import useMe from '@/hooks/useMe.js';
import { getPolicyProducer, setPolicyProducer, setPolicyProducerShare } from '@/services/deals.service.js';
import { percentToShare, shareToPercent } from '@/lib/producer-share.js';

// The producer of this policy, beside the fees key in the deal header (deals-door-1 T16, PLAN-FINAL 4.1, D-15, D-15b).
// A TRACON user, held in TRACON only. Everyone signed in sees the name, or "Not stated". Only the owner's own sign-in
// (the server's /auth/me `owner`, isPersonalOwner) gets the picker; the server's PATCH is the gate, this only shows it.
// The server's answer is rendered as given: the assignable list is the server's, each warning (a deal that has closed,
// paperwork and money, credits the producer named now) is one toast.info beside the success toast.
// Round 3 (R2c-Q1): the producer's share on this policy, as the server's one rule resolves it (the policy's own share, else the
// producer's rate for new business or renewal, else "share not stated"); the owner may set this policy's own share or clear it.
const UNASSIGNED = '';
const KIND_WORDS = { new_business: 'new business', renewal: 'renewal' };
function shareLine(share) {
  if (!share || share.state !== 'ok') return 'share: could not check';
  if (share.share === null || share.share === undefined) return 'share not stated';
  const pct = `${shareToPercent(share.share)}%`;
  return share.source === 'policy_override' ? `share ${pct} (this policy)` : `share ${pct} (${KIND_WORDS[share.kind] || 'producer'} rate)`;
}

export default function DealProducerKey({ dealId }) {
  const me = useMe();
  const owner = !!me && me.kind === 'personal' && me.owner === true;
  const [state, setState] = useState({ status: 'loading', data: null });
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState(UNASSIGNED);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [sharePct, setSharePct] = useState('');
  const [shareReason, setShareReason] = useState('');

  useEffect(() => {
    if (!dealId) return undefined;
    let alive = true;
    setState({ status: 'loading', data: null });
    getPolicyProducer(dealId)
      .then((d) => { if (alive) setState({ status: 'ok', data: d }); })
      .catch((err) => { if (alive) setState({ status: err?.status === 404 ? 'no_policy' : 'could_not_check', data: null }); });
    return () => { alive = false; };
  }, [dealId]);

  useEffect(() => {
    if (!open || state.status !== 'ok') return;
    setChoice(state.data?.producer?.user_id || UNASSIGNED);
    setReason('');
    setSharePct(shareToPercent(state.data?.share?.override));
    setShareReason('');
  }, [open, state]);

  const base = 'inline-flex items-center gap-1 rounded px-1 py-px text-xs';
  if (state.status === 'loading') return <span className={`${base} text-text-dim`} data-deal-producer="loading">producer: loading</span>;
  if (state.status === 'no_policy') return <span className={`${base} text-text-dim`} data-deal-producer="no_policy" title="TRACON holds no policy record for this deal">producer: no TRACON policy</span>;
  if (state.status !== 'ok') return <span className={`${base} text-text-dim`} data-deal-producer="could_not_check" title="The producer could not be read">producer: could not check</span>;

  const current = state.data?.producer || null;
  const word = current ? `producer: ${current.label || 'unnamed user'}, ${shareLine(state.data?.share)}` : 'producer: Not stated';
  if (!owner) return <span className={`${base} text-text-muted`} data-deal-producer={current ? 'stated' : 'not_stated'}>{word}</span>;

  const assignable = Array.isArray(state.data?.assignable) ? state.data.assignable : [];
  const heldElsewhere = current && !assignable.some((u) => u.user_id === current.user_id);
  const clearing = choice === UNASSIGNED;
  const unchanged = choice === (current?.user_id || UNASSIGNED);

  async function save() {
    setBusy(true);
    try {
      const res = await setPolicyProducer(dealId, { userId: clearing ? null : choice, reason: reason.trim() || null });
      if (!res?.ok) throw new Error(res?.message || res?.error || 'failed');
      toast.success(res.producer ? `Producer saved: ${res.producer.label || 'unnamed user'}` : 'Producer cleared: Not stated');
      for (const w of res.warnings || []) toast.info(w);
      // The share follows the producer (their own rate): the server's answer carries it, never derived here.
      setState({ status: 'ok', data: { ...state.data, producer: res.producer, share: res.share } });
      setOpen(false);
    } catch (err) {
      toast.error(`Producer not saved: ${err?.body?.message || err?.message || 'failed'}`);
    } finally {
      setBusy(false);
    }
  }

  const heldShare = state.data?.share?.override ?? null;
  const typed = percentToShare(sharePct);
  const shareClearing = typed.ok && typed.share === null && heldShare !== null;
  const shareUnchanged = !typed.ok || typed.share === heldShare;
  async function saveShare() {
    setBusy(true);
    try {
      const res = await setPolicyProducerShare(dealId, { share: typed.share, reason: shareReason.trim() || null });
      if (!res?.ok) throw new Error(res?.message || res?.error || 'failed');
      toast.success(typed.share === null ? 'This policy\'s share cleared: the producer\'s rate applies' : `This policy's share saved: ${shareToPercent(typed.share)}%`);
      for (const w of res.warnings || []) toast.info(w);
      setState({ status: 'ok', data: { ...state.data, share: res.share } });
      setOpen(false);
    } catch (err) {
      toast.error(`Share not saved: ${err?.body?.message || err?.message || 'failed'}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title="Set this policy's producer" data-deal-producer={current ? 'stated' : 'not_stated'}
          className={`${base} hover:bg-surface-strong ${current ? 'font-medium text-text' : 'text-text-muted hover:text-text'}`}>
          {word}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" sideOffset={4} className="w-80 p-3">
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-dim">Producer</div>
        <label className="mb-2 block text-xs text-text-muted">
          Who is credited for this policy
          <select aria-label="Producer" value={choice} onChange={(e) => setChoice(e.target.value)}
            className="mt-1 w-full rounded-card border border-border bg-bg px-2 py-1 text-sm text-text">
            <option value={UNASSIGNED}>Unassigned (Not stated)</option>
            {heldElsewhere ? <option value={current.user_id} disabled>{current.label || 'unnamed user'} (no longer assignable)</option> : null}
            {assignable.map((u) => <option key={u.user_id} value={u.user_id}>{u.label}</option>)}
          </select>
        </label>
        <label className="mb-3 block text-xs text-text-muted">
          {clearing ? 'Reason (required to clear)' : 'Reason (kept on the activity line)'}
          <Input aria-label="Producer reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. took the account over" className="mt-1" />
        </label>
        <div className="flex justify-end">
          <Button type="button" size="sm" disabled={busy || unchanged || (clearing && !reason.trim())} onClick={save}>
            {busy ? 'Saving...' : 'Save'}
          </Button>
        </div>
        <div className="mb-2 mt-4 border-t border-border pt-3 text-[10px] font-semibold uppercase tracking-wider text-text-dim">Producer share on this policy</div>
        <p className="mb-2 text-xs text-text-muted" data-deal-producer-share>{shareLine(state.data?.share)}. Blank uses the producer's own rate.</p>
        <label className="mb-2 block text-xs text-text-muted">
          This policy's share, %
          <Input aria-label="Producer share on this policy" inputMode="decimal" value={sharePct} onChange={(e) => setSharePct(e.target.value)} placeholder="the producer's rate" className="mt-1" />
        </label>
        {!typed.ok ? <p className="mb-2 text-xs text-accent">A share is a percent from 0 to 100, or blank.</p> : null}
        <label className="mb-3 block text-xs text-text-muted">
          {shareClearing ? 'Reason (required to clear)' : 'Reason (kept on the activity line)'}
          <Input aria-label="Producer share reason" value={shareReason} onChange={(e) => setShareReason(e.target.value)} className="mt-1" />
        </label>
        <div className="flex justify-end">
          <Button type="button" size="sm" disabled={busy || shareUnchanged || (shareClearing && !shareReason.trim())} onClick={saveShare}>
            {busy ? 'Saving...' : 'Save share'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
